/** Shapes the internal apis answer with, as adminwebapi passes them through. */

export interface AuthResult {
  status: number;
  token: string;
  expires_at: number;
  admin_id: number;
}

export interface Me {
  status: number;
  admin_id: number;
  open_tickets: number;
}

export type TicketStatus = "open" | "closed";
export type TicketAuthor = "client" | "support" | "bot";
export type TicketMessageKind = "text" | "attachment";
export type DeliveryStatus = "pending" | "delivered" | "failed";

export interface Ticket {
  id: number;
  user_id: number;
  status: TicketStatus;
  language: string | null;
  /** Whether the writer is registered in the main bot. Null when nobody could find out. */
  is_client: boolean | null;
  username: string | null;
  first_name: string | null;
  messages_count: number;
  /** The client blocked the support bot: answers do not reach them. */
  client_blocked: boolean;
  created_at: string;
  last_message_at: string;
  last_client_message_at: string | null;
  last_support_message_at: string | null;
  closed_at: string | null;
  closed_by_admin_id: number | null;
}

export interface TicketListItem extends Ticket {
  /** Last line of the conversation, so the list reads without opening every request. */
  preview: string | null;
}

export interface TicketMessage {
  id: number;
  ticket_id: number;
  author: TicketAuthor;
  kind: TicketMessageKind;
  content_type: string | null;
  text: string | null;
  telegram_message_id: number | null;
  admin_id: number | null;
  request_id: string | null;
  delivery_status: DeliveryStatus | null;
  status_code: number | null;
  status_message: string | null;
  created_at: string;
}

/** Both halves, always: the list is ordered by two columns and a cursor of one skips rows. */
export interface TicketsCursor {
  before_at: string;
  before_id: number;
}

export interface TicketsPage {
  status: number;
  tickets: TicketListItem[];
  cursor: TicketsCursor | null;
}

export interface TicketsCount {
  status: number;
  count: number;
}

export interface TicketThread {
  status: number;
  ticket: Ticket;
  messages: TicketMessage[];
}

export interface TicketReplyResult {
  status: number;
  ticket: Ticket;
  message: TicketMessage | null;
}

export type PaymentStatus = "created" | "paid" | "cancelled" | "requested" | "refunding" | "refunded" | "rejected";

/**
 * Statuses a payment can be given back in.
 *
 * The same list as REFUNDABLE_STATUSES in Admin/admintgfront/app/commands/refunds.py — the bot
 * and this application offer the same payments, and if one of them ever changes so must the
 * other. A rejected payment is among them: the client can not ask for it again, and a refund by
 * hand from here is the way back.
 */
export const REFUNDABLE_STATUSES: readonly PaymentStatus[] = ["paid", "requested", "refunding", "rejected"];

export type PackageKind = "tokens" | "requests_limit" | "chats_limit" | "trackings_limit";

export interface Payment {
  id: number;
  user_id: number;
  status: PaymentStatus;
  package_id: string;
  kind: PackageKind;
  tokens: number;
  limit_increment: number;
  price_amount: number;
  price_currency: string;
  provider_charge_id: string | null;
  unrecovered_tokens: number;
  refund_reason: string | null;
  refund_requested_at: string | null;
  /** Set once an admin turned the client's request down. Absent from a backend older than that. */
  refund_rejected_at?: string | null;
  created_at: string;
  paid_at: string | null;
  refunded_at: string | null;
}

export interface PaymentsPage {
  status: number;
  payments: Payment[];
}

/**
 * What giving one payment back would cost, counted by clientbackapi at this moment.
 *
 * Passed through by adminwebapi untouched, so the fields are the ones of RefundPreparationResponse
 * in Client/clientbackapi/app/models/api.py. The token amounts are in hundredths of a token and
 * add up: clawback_tokens + shortfall = tokens.
 */
export interface RefundPreparation {
  status: number;
  payment: Payment;
  /** What the package credited. 0 for a bought limit. */
  tokens: number;
  /** What can be taken off the balance right now. */
  clawback_tokens: number;
  /** What the client has already spent: the stars go back in full, these tokens do not. */
  shortfall: number;
  previous_refunds_count: number;
  /** What a bought limit becomes after the refund, null for a package of tokens. */
  limit_after?: number | null;
}

export interface RefundResult {
  status: number;
  payment: Payment;
  message: string;
}

/**
 * A request turned down. `notified` is false when the client did not get the message about it:
 * the rejection stands either way, which is why that is a success saying so and not an error.
 */
export interface RefundRejectResult {
  status: number;
  payment: Payment;
  notified: boolean;
  message: string;
}

export interface ScraperChat {
  number?: number;
  chat_id?: number;
  dialog_id?: number;
  chat_title?: string;
  title?: string;
  chat_username_or_hash?: string;
}

/**
 * What taking one chat off monitoring would cost.
 *
 * The three counts are why blocking is a two step action: "снять чат" says nothing about how
 * many people it affects or how much of other people's stored messages it deletes.
 */
export interface BlockedChatPreview {
  chat_id: number;
  chat_title: string | null;
  trackings_count: number;
  clients_count: number;
  notifications_count: number;
  is_blocked: boolean;
}

/** What came of putting a message author who objected on the list of excluded authors. */
export interface BlockedAuthorResult {
  status: number;
  message?: string;
  /** False when the author already was on the list: the call succeeded and changed nothing. */
  newly_blocked: boolean;
}

/** What came of one grant attempt. */
export interface GrantResult {
  status: number;
  user_id: number;
  tokens: number;
  request_id: string;
  /**
   * Whether this call is what credited it. False for a replay of the same attempt: the tokens
   * are there, an earlier call put them there, and the client was told once.
   */
  credited: boolean;
  /**
   * Whether the client was told. False means the money landed and the message did not, which
   * is the one outcome an admin must not read as a failure and repeat.
   */
  notified: boolean;
  message: string;
}

/** The windows of the statistics: a day by hour, a month by day, a year by month. */
export type StatsPeriod = "day" | "month" | "year";

/** One bar of the statistics: who registered and who spent in that hour, day or month. */
export interface StatsBucket {
  /** Where the bucket starts and where the next one does, utc with the offset written out. */
  start: string;
  end: string;
  new_users: number;
  /** Clients with at least one finished spend in the bucket, each counted once. */
  active_users: number;
  /** Every client registered by the end of the bucket, the ones from before the window included. */
  total_users: number;
  /**
   * `active_users` less the active clients of the bucket before - of the one before the window,
   * for the first bucket. Below zero when fewer were active.
   */
  active_change: number;
}

/**
 * New and active clients over a rolling window that ends with the current hour, day or month.
 *
 * Every bucket is there, the empty ones included, oldest first, and the last one is the current
 * one - not over yet, which `now` says how far. `totals.active_users` is the clients active at
 * least once in the whole window and not the sum of the bars: somebody active every day of a
 * month is one active client of that month.
 */
export interface UsersStats {
  status: number;
  period: StatsPeriod;
  unit: "hour" | "day" | "month";
  buckets: StatsBucket[];
  totals: { new_users: number; active_users: number };
  now: string;
}
